// Generic "where do I go on back?" mechanism for v2 show
// pages. The previous design hardcoded one specific
// breadcrumb source (`from_conversation_id`), which broke
// any flow deeper than one hop — see CON-85 for the
// concrete bug: conversation → contact → deal → back chevron
// dropped the user on the pipeline because the `from_*`
// param was not propagated from contact to deal.
//
// Solution: a single `return_to=<urlencoded path>` query
// param that every show page reads on back and propagates
// (with itself prepended) on forward links. Because the
// nested `return_to` is itself URL-encoded inside the
// outer one, the chain composes naturally to arbitrary
// depth — each pop reveals the next return_to underneath.
//
// All state lives in the URL: refresh-proof, deep-linkable,
// no sessionStorage, no React context.

const SAME_ORIGIN_PREFIX = "/"
const RETURN_TO_PARAM = "return_to"

// Parses a candidate `return_to` value. Only accepts
// same-origin relative paths ("/v2/...") to prevent
// open-redirect — a malicious link could otherwise carry
// `return_to=https://evil.com` and trick the user into
// clicking a familiar-looking back chevron that hops to
// an attacker site.
export function parseReturnTo(raw: string | null): string | null {
  if (!raw) return null
  if (typeof raw !== "string") return null
  if (!raw.startsWith(SAME_ORIGIN_PREFIX)) return null
  if (raw.startsWith("//")) return null
  return raw
}

// Reads the `return_to` from a search string (e.g.
// `?return_to=%2Fv2%2Finbox`). SSR-safe — accepts an
// explicit search string so tests don't need to mutate
// window.location.
export function readReturnTo(search: string): string | null {
  const params = new URLSearchParams(search)
  return parseReturnTo(params.get(RETURN_TO_PARAM))
}

// Builds a forward-navigation href that propagates the
// existing return_to chain by URL-encoding the *current*
// location as the new `return_to`. The result is the
// destination URL with one more level of `return_to`
// nesting.
//
// Example:
//   buildForwardHref({
//     destination: "/v2/deals/7",
//     currentHref: "/v2/contacts/5?return_to=%2Fv2%2Finbox%3Fconversation_id%3D42",
//   })
//   → "/v2/deals/7?return_to=%2Fv2%2Fcontacts%2F5%3Freturn_to%3D%252Fv2%252Finbox%253Fconversation_id%253D42"
//
// On the deal-show page, reading `return_to` yields
// "/v2/contacts/5?return_to=%2Fv2%2Finbox%3Fconversation_id%3D42",
// which itself still carries the conversation in its
// `return_to`. Back from deal → contact → conversation.
export function buildForwardHref({
  destination,
  currentHref,
}: {
  destination: string
  currentHref: string
}): string {
  if (!currentHref) return destination
  // Same-origin protection: refuse to encode anything that
  // isn't a relative path. Defensive — the caller should
  // already be passing a relative href.
  if (!currentHref.startsWith(SAME_ORIGIN_PREFIX)) return destination
  if (currentHref.startsWith("//")) return destination

  const [destPath, destQuery = ""] = destination.split("?")
  const params = new URLSearchParams(destQuery)
  params.set(RETURN_TO_PARAM, currentHref)
  return `${destPath}?${params.toString()}`
}

// Reads `window.location` for the current href. SSR-safe.
// Used by client-side helpers/hooks; tests pass an explicit
// href to `buildForwardHref` directly.
export function currentHref(): string {
  if (typeof window === "undefined") return ""
  return `${window.location.pathname}${window.location.search}`
}

// Backward-compat shim for the old `from_conversation_id`
// param. New links use `return_to`, but old bookmarks /
// shared URLs / agent-published links from before CON-85
// still carry `from_conversation_id`. We honor them for
// one release with a console.warn, then drop the shim.
export function legacyConversationReturnTo(
  search: string,
): string | null {
  const params = new URLSearchParams(search)
  const raw = params.get("from_conversation_id")
  if (!raw) return null
  const id = Number.parseInt(raw, 10)
  if (!Number.isInteger(id) || id <= 0) return null
  if (typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[v2] from_conversation_id is deprecated; use return_to",
    )
  }
  return `/v2/inbox?conversation_id=${id}`
}

// Resolves the back href for a show page. Reads `return_to`
// from the URL; if absent, falls back to the page's parent
// index. Honors the legacy `from_conversation_id` shim
// so existing bookmarks keep working.
export function resolveBackHref({
  search,
  fallback,
}: {
  search: string
  fallback: string
}): string {
  return (
    readReturnTo(search) ??
    legacyConversationReturnTo(search) ??
    fallback
  )
}
